import { useState, type ReactNode } from 'react';
import { Check, Pencil } from 'lucide-react';

import { Button } from 'components/ui/button';
import { cn } from 'lib/utils';

export interface ChoicePromptOption {
  value: string;
  label: string;
  icon?: ReactNode;
}

export interface ChoicePromptCardProps {
  options: ChoicePromptOption[];
  /** Single mode (default): a click answers immediately via onSelect. */
  onSelect?: (option: ChoicePromptOption) => void;
  /** Multi mode: pills toggle with a checkbox and the Submit button answers.
   *  Submit also sends any typed "Something Else" text. */
  multiple?: boolean;
  /** Shows a Submit button. In single mode it sends the typed "Something Else" text. */
  submitLabel?: string;
  onSubmit?: (options: ChoicePromptOption[], otherText: string) => void;
  /** Free-text footer input, e.g. "Something Else" — hidden when no label is given.
   *  Enter submits the typed text. */
  otherLabel?: string;
  onOtherSubmit?: (text: string) => void;
  /** Footer action, e.g. "Skip for now" — hidden when no label is given. */
  skipLabel?: string;
  onSkip?: () => void;
  disabled?: boolean;
  className?: string;
  /** Names the chip group for screen readers, e.g. the question it answers. */
  ariaLabel?: string;
  /** Single mode: marks the chosen chip as pressed. */
  selectedValue?: string;
}

/** Pill choices with an optional free-text / skip / submit footer. */
export function ChoicePromptCard({
  options,
  onSelect,
  multiple,
  submitLabel,
  onSubmit,
  otherLabel,
  onOtherSubmit,
  skipLabel,
  onSkip,
  disabled,
  className,
  ariaLabel,
  selectedValue,
}: Readonly<ChoicePromptCardProps>) {
  const ChipGroup = ariaLabel ? 'fieldset' : 'div';
  const [otherText, setOtherText] = useState('');
  const [selected, setSelected] = useState<string[]>([]);
  const showSubmit = !!submitLabel && (multiple || !!otherLabel);
  const showFooter = !!otherLabel || !!skipLabel || showSubmit;

  const submitOther = () => {
    const text = otherText.trim();
    if (!text || disabled) return;
    onOtherSubmit?.(text);
    setOtherText('');
  };

  const handleSubmit = () => {
    if (!multiple) {
      submitOther();
      return;
    }
    onSubmit?.(
      options.filter((option) => selected.includes(option.value)),
      otherText.trim()
    );
    setOtherText('');
  };

  const handleOptionClick = (option: ChoicePromptOption) => {
    if (!multiple) {
      onSelect?.(option);
      return;
    }
    setSelected((prev) =>
      prev.includes(option.value)
        ? prev.filter((value) => value !== option.value)
        : [...prev, option.value]
    );
  };

  return (
    <div
      className={cn(
        'flex w-full flex-col gap-5 rounded-card border border-grey-100 bg-white px-5 py-4 shadow-choice-card dark:border-border dark:bg-card',
        className
      )}
    >
      {/* A named chip row is a fieldset (implicit group role); the reset keeps the row's layout. */}
      <ChipGroup
        className="m-0 flex min-w-0 flex-wrap items-center gap-2 border-0 p-0"
        aria-label={ariaLabel}
      >
        {options.map((option) => {
          const isSelected = multiple
            ? selected.includes(option.value)
            : selectedValue === option.value;
          return (
            <button
              key={option.value}
              type="button"
              disabled={disabled}
              aria-pressed={multiple || selectedValue !== undefined ? isSelected : undefined}
              onClick={() => handleOptionClick(option)}
              className={cn(
                'flex items-center gap-2 rounded-full border px-3 py-2 transition-colors hover:bg-grey-50 disabled:opacity-50 dark:border-border dark:text-muted-foreground dark:hover:bg-muted',
                multiple
                  ? 'border-choice-border text-xs text-choice-text'
                  : 'border-grey-100 text-sm text-grey-600',
                !multiple && isSelected && 'bg-grey-50 dark:bg-muted'
              )}
            >
              {option.icon ? <span className="flex size-4 shrink-0">{option.icon}</span> : null}
              {option.label}
              {multiple ? (
                <span
                  className={cn(
                    'flex size-5 shrink-0 items-center justify-center rounded-full',
                    isSelected
                      ? 'bg-black-base text-white dark:bg-foreground dark:text-background'
                      : 'border-2 border-grey-200'
                  )}
                >
                  {isSelected ? <Check className="size-3" strokeWidth={3} /> : null}
                </span>
              ) : null}
            </button>
          );
        })}
      </ChipGroup>

      {showFooter ? (
        <div className="flex items-center justify-between gap-3 rounded-lg border border-grey-50 px-3 py-2 dark:border-border">
          {otherLabel ? (
            <label className="flex min-w-0 flex-1 items-center gap-3">
              <span className="flex size-5 shrink-0 items-center justify-center rounded bg-grey-100 dark:bg-muted">
                <Pencil className="size-3 text-grey-500" strokeWidth={1.75} />
              </span>
              <input
                type="text"
                value={otherText}
                disabled={disabled}
                placeholder={otherLabel}
                aria-label={otherLabel}
                onChange={(event) => setOtherText(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === 'Enter') {
                    event.preventDefault();
                    submitOther();
                  }
                }}
                className="min-w-0 flex-1 bg-transparent text-sm leading-heading text-grey-900 outline-none placeholder:text-grey-400 disabled:opacity-50 dark:text-foreground"
              />
            </label>
          ) : (
            <span />
          )}
          <div className="flex shrink-0 items-center gap-2">
            {skipLabel ? (
              <Button type="button" variant="outline" size="sm" disabled={disabled} onClick={onSkip}>
                {skipLabel}
              </Button>
            ) : null}
            {showSubmit ? (
              <Button
                type="button"
                size="sm"
                disabled={disabled || (selected.length === 0 && !otherText.trim())}
                onClick={handleSubmit}
              >
                {submitLabel}
              </Button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}
